import type { PathWatcherEvent, WebContainer } from '@webcontainer/api';
import { getEncoding } from 'istextorbinary';
import { map, type MapStore } from 'nanostores';
import { Buffer } from 'node:buffer';
import * as nodePath from 'node:path';
import { bufferWatchEvents } from '~/utils/buffer';
import { WORK_DIR } from '~/utils/constants';
import { computeFileModifications } from '~/utils/diff';
import { createScopedLogger } from '~/utils/logger';
import { unreachable } from '~/utils/unreachable';
import { storeFileActions } from '~/lib/persistence/index';
import type { StoreFileAction } from '~/types/actions';
import { chatState, type ChatStatus } from '~/lib/stores/chatState';

const logger = createScopedLogger('FilesStore');

const utf8TextDecoder = new TextDecoder('utf8', { fatal: true });

export interface File {
  type: 'file';
  content: string;
  isBinary: boolean;
}

export interface Folder {
  type: 'folder';
}

type Dirent = File | Folder;

export type FileMap = Record<string, Dirent | undefined>;

export class FilesStore {
  #webcontainer: Promise<WebContainer>;
  #chatStatus: ChatStatus = chatState.get().status;
  #unsubChatState?: () => void;

  /**
   * Debounce and coalescing controls for updating file actions in DB.
   */
  #updateTimer?: ReturnType<typeof setTimeout>;
  #updateInProgress = false;
  #updateQueued = false;
  #debouncedUpdateFileActionsInDb!: () => void;

  /**
   * Timestamp of the most recent manual write via saveFile(). Used to infer batch origin.
   */
  #lastManualWriteAt = 0;

  /**
   * Origin decided for the current buffered batch handled in #processEventBuffer.
   */
  #currentBatchOrigin: 'ai' | 'manual' = 'ai';

  /**
   * Tracks the number of files without folders.
   */
  #size = 0;

  /**
   * @note Keeps track all modified files with their original content since the last user message.
   * Needs to be reset when the user sends another message and all changes have to be submitted
   * for the model to be aware of the changes.
   */
  #modifiedFiles: Map<string, string> = import.meta.hot?.data.modifiedFiles ?? new Map();

  /**
   * Tracks files that have changed since the last DB update and need to be persisted.
   */
  #pendingDbUpdates: Set<string> = import.meta.hot?.data.pendingDbUpdates ?? new Set();

  /**
   * Map of files that matches the state of WebContainer.
   */
  files: MapStore<FileMap> = import.meta.hot?.data.files ?? map({});

  get filesCount() {
    return this.#size;
  }

  constructor(webcontainerPromise: Promise<WebContainer>) {
    this.#webcontainer = webcontainerPromise;

    if (import.meta.hot) {
      import.meta.hot.data.files = this.files;
      import.meta.hot.data.modifiedFiles = this.#modifiedFiles;
      import.meta.hot.data.pendingDbUpdates = this.#pendingDbUpdates;
    }

    // keep local chat status in sync with global store for up-to-date checks
    this.#unsubChatState = chatState.subscribe((value) => {
      this.#chatStatus = value.status;
      console.log('[files] filesStore chat status: ', this.#chatStatus);

      if (this.#chatStatus === 'ready') {
        console.log(
          '[ready] filesStore chat status is ready, inside the constructor, triggering updateFileActionsInDb',
        );
        this.updateFileActionsInDb();
      }
    });

    this.#init();
  }

  getFile(filePath: string) {
    const dirent = this.files.get()[filePath];

    if (dirent?.type !== 'file') {
      return undefined;
    }

    return dirent;
  }

  getFileModifications() {
    return computeFileModifications(this.files.get(), this.#modifiedFiles);
  }

  getModifiedFiles() {
    let modifiedFiles: { [path: string]: File } | undefined = undefined;

    for (const [filePath, originalContent] of this.#modifiedFiles) {
      const file = this.files.get()[filePath];

      if (file?.type !== 'file') {
        continue;
      }

      if (file.content === originalContent) {
        continue;
      }

      if (!modifiedFiles) {
        modifiedFiles = {};
      }

      modifiedFiles[filePath] = file;
    }

    return modifiedFiles;
  }

  resetFileModifications() {
    this.#modifiedFiles.clear();
  }

  async saveFile(filePath: string, content: string) {
    console.log('[files] [api] saveFile filestore:', filePath, content);

    const webcontainer = await this.#webcontainer;

    try {
      const relativePath = nodePath.relative(webcontainer.workdir, filePath);

      if (!relativePath) {
        throw new Error(`[files] EINVAL: invalid file path, write '${relativePath}'`);
      }

      const oldContent = this.getFile(filePath)?.content;

      if (!oldContent) {
        unreachable('[files] Expected content to be defined');
      }

      await webcontainer.fs.writeFile(relativePath, content);

      if (!this.#modifiedFiles.has(filePath)) {
        this.#modifiedFiles.set(filePath, oldContent);
      }

      // we immediately update the file and don't rely on the `change` event coming from the watcher
      this.files.setKey(filePath, { type: 'file', content, isBinary: false });

      // Track this file for DB update
      this.#pendingDbUpdates.add(filePath);

      // Mark that a manual write just happened; the next buffered batch is manual
      this.#lastManualWriteAt = Date.now();

      logger.info('[files] File updated');
    } catch (error) {
      logger.error('[files] Failed to update file content\n\n', error);

      throw error;
    }
  }

  async #init() {
    const webcontainer = await this.#webcontainer;

    webcontainer.internal.watchPaths(
      { include: [`${WORK_DIR}/**`], exclude: ['**/node_modules', '.git'], includeContent: true },
      bufferWatchEvents(100, this.#processEventBuffer.bind(this)),
    );
  }

  async updateFileActionsInDb() {
    const fileActions: StoreFileAction[] = [];

    console.log('[ready] [updateFileActionsInDb] pendingDbUpdates size: ', this.#pendingDbUpdates.size);

    const allFiles = this.files.get();

    // Snapshot the current set to avoid iteration issues if the set changes mid-flight
    const processedPaths = Array.from(this.#pendingDbUpdates);

    console.log('[files] [updateFileActionsInDb] before for loop');

    for (const filePath of processedPaths) {
      const dirent = allFiles[filePath];
      console.log('[files] [updateFileActionsInDb] filePath: ', filePath);

      if (dirent?.type === 'file') {
        // remove /home/project/ from starting of the filePath
        const relativePath = filePath.slice(WORK_DIR.length + 1);
        fileActions.push({ type: 'file', filePath: relativePath, content: dirent.content, id: relativePath });
      } else if (dirent === undefined) {
        // File was deleted, mark it with deleted flag
        console.log('[files] [updateFileActionsInDb] file was deleted: ', filePath);

        const relativePath = filePath.slice(WORK_DIR.length + 1);
        fileActions.push({ type: 'file', filePath: relativePath, content: '', id: relativePath, deleted: true });
      }
    }

    console.log('[files] [updateFileActionsInDb] after for loop');

    try {
      if (fileActions.length > 0) {
        console.log('[files] [updateFileActionsInDb] starting storeFileActions with count:', fileActions.length);
        await storeFileActions(fileActions, this.#currentBatchOrigin);
      }
    } catch (error) {
      console.error('[files] [updateFileActionsInDb] storeFileActions failed:', error);
    } finally {
      // Clear only the entries we just processed; keep any newly added ones
      console.log('[files] [updateFileActionsInDb] clearing processed pendingDbUpdates');

      for (const filePath of processedPaths) {
        this.#pendingDbUpdates.delete(filePath);
      }
    }
  }

  #processEventBuffer(events: Array<[events: PathWatcherEvent[]]>) {
    const watchEvents = events.flat(2);

    // console.log('[files] processEventBuffer hitted: ', watchEvents);

    // Decide batch origin for this buffered set of events based on recency of manual writes
    const isManualBatch = Date.now() - this.#lastManualWriteAt < 1500; // 1.5s window
    this.#currentBatchOrigin = isManualBatch ? 'manual' : 'ai';

    console.log('[ready] [processEventBuffer] currentBatchOrigin: ', this.#currentBatchOrigin);

    for (const { type, path, buffer } of watchEvents) {
      // remove any trailing slashes
      const sanitizedPath = path.replace(/\/+$/g, '');
      console.log('[files] [processEventBuffer] inside for loop: ', sanitizedPath, type);

      switch (type) {
        case 'add_dir': {
          // we intentionally add a trailing slash so we can distinguish files from folders in the file tree
          this.files.setKey(sanitizedPath, { type: 'folder' });
          break;
        }
        case 'remove_dir': {
          this.files.setKey(sanitizedPath, undefined);

          for (const [direntPath] of Object.entries(this.files)) {
            if (direntPath.startsWith(sanitizedPath)) {
              this.files.setKey(direntPath, undefined);
            }
          }

          break;
        }
        case 'add_file':
        case 'change': {
          if (type === 'add_file') {
            this.#size++;
          }

          let content: string = '';

          /**
           * @note This check is purely for the editor. The way we detect this is not
           * bullet-proof and it's a best guess so there might be false-positives.
           * The reason we do this is because we don't want to display binary files
           * in the editor nor allow to edit them.
           */
          const isBinary = isBinaryFile(buffer);

          if (!isBinary) {
            content = this.#decodeFileContent(buffer);
          } else {
            content = buffer ? Buffer.from(buffer).toString('base64') : '';
          }

          // console.log('[files] content >>>>>>', content);

          this.files.setKey(sanitizedPath, { type: 'file', content, isBinary });

          // Track this file for DB update
          this.#pendingDbUpdates.add(sanitizedPath);

          break;
        }
        case 'remove_file': {
          this.#size--;
          this.files.setKey(sanitizedPath, undefined);

          // Track this file for DB update (to sync deletion)
          this.#pendingDbUpdates.add(sanitizedPath);

          break;
        }
        case 'update_directory': {
          // we don't care about these events
          break;
        }
      }
    }

    // Only persist file actions when chat is not actively streaming/submitted
    console.log('[files] filesStore chat status: ', this.#chatStatus);

    if (this.#chatStatus === 'ready') {
      console.log('[ready] filesStore chat status is ready, scheduling debounced update file actions in db');
      this.updateFileActionsInDb();
    }
  }

  #decodeFileContent(buffer?: Uint8Array) {
    if (!buffer || buffer.byteLength === 0) {
      return '';
    }

    try {
      return utf8TextDecoder.decode(buffer);
    } catch (error) {
      /*
       * Sentry.captureException(error, {
       *   extra: {
       *     message: 'Error from filestore in decoding file content',
       *     buffer,
       *   },
       *   level: 'log',
       * });
       */
      console.log(error);
      return '';
    }
  }
}

function isBinaryFile(buffer: Uint8Array | undefined) {
  if (buffer === undefined) {
    return false;
  }

  return getEncoding(convertToBuffer(buffer), { chunkLength: 100 }) === 'binary';
}

/**
 * Converts a `Uint8Array` into a Node.js `Buffer` by copying the prototype.
 * The goal is to  avoid expensive copies. It does create a new typed array
 * but that's generally cheap as long as it uses the same underlying
 * array buffer.
 */
function convertToBuffer(view: Uint8Array): Buffer {
  const buffer = new Uint8Array(view.buffer, view.byteOffset, view.byteLength);

  Object.setPrototypeOf(buffer, Buffer.prototype);

  return buffer as Buffer;
}
